import React from 'react';
import { motion } from 'framer-motion';
import { Apple, Play } from 'lucide-react';
export const AppBadge = ({
  platform,
  className = '',
  href = 'https://app.kdx.ng'
}: {
  platform: 'ios' | 'android';
  className?: string;
  href?: string;
}) => {
  const isIOS = platform === 'ios';
  return <motion.a href={href} target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.05,
    y: -2
  }} whileTap={{
    scale: 0.95
  }} className={`flex items-center gap-3 bg-black border border-white/20 rounded-xl px-4 py-2.5 hover:border-purple-500/50 hover:shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all no-underline ${className}`} data-id="element-150">
      {isIOS ? <Apple className="w-8 h-8 text-white" fill="currentColor" data-id="element-151" /> : <Play className="w-7 h-7 text-white" fill="currentColor" data-id="element-152" />}
      <div className="text-left flex flex-col justify-center" data-id="element-153">
        <span className="text-[10px] leading-none text-gray-400 font-medium mb-1" data-id="element-154">
          {isIOS ? 'Download on the' : 'GET IT ON'}
        </span>
        <span className="text-sm leading-none text-white font-semibold" data-id="element-155">
          {isIOS ? 'App Store' : 'Google Play'}
        </span>
      </div>
    </motion.a>;
};
export const PhoneMockup = ({
  screenName,
  children,
  className = ''
}: {
  screenName: string;
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={`relative w-[280px] h-[580px] rounded-[40px] border-[8px] border-gray-900 bg-gray-950 shadow-2xl overflow-hidden flex flex-col ${className}`} data-id="element-156">
      {/* Notch */}
      <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-157">
        <div className="w-32 h-6 bg-gray-900 rounded-b-2xl" data-id="element-158"></div>
      </div>

      {/* Screen Content */}
      <div className="flex-1 relative z-10 flex flex-col w-full h-full bg-[#0a0e27]" data-id="element-159">
        {/* Status Bar Placeholder */}
        <div className="h-12 w-full flex items-end justify-between px-6 pb-2 text-[10px] text-white/70 font-medium" data-id="element-160">
          <span data-id="element-161">9:41</span>
          <div className="flex gap-1" data-id="element-162">
            <span data-id="element-163">5G</span>
            <span data-id="element-164">100%</span>
          </div>
        </div>

        {/* App Header */}
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between" data-id="element-165">
          <span className="font-bold text-white tracking-wide" data-id="element-166">KDX</span>
          <span className="text-xs text-purple-400 font-medium" data-id="element-167">
            {screenName}
          </span>
        </div>

        {/* Dynamic Content */}
        <div className="flex-1 overflow-hidden relative" data-id="element-168">{children}</div>

        {/* Home Indicator */}
        <div className="absolute bottom-2 inset-x-0 flex justify-center z-20" data-id="element-169">
          <div className="w-24 h-1 bg-white/30 rounded-full" data-id="element-170"></div>
        </div>
      </div>
    </div>;
};
export const GlassCard = ({
  children,
  className = '',
  hover = true
}: {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
}) => {
  return <motion.div whileHover={hover ? {
    y: -4
  } : {}} className={`glass-panel rounded-2xl p-6 transition-colors duration-300 hover:bg-white/[0.05] ${className}`} data-id="element-171">
      {children}
    </motion.div>;
};
export const Button = ({
  children,
  variant = 'primary',
  className = '',
  ...props
}: any) => {
  const baseStyle = 'px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2';
  const variants = {
    primary: 'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)]',
    secondary: 'bg-white/10 text-white hover:bg-white/20 border border-white/10',
    outline: 'border border-purple-500/50 text-purple-400 hover:bg-purple-500/10'
  };
  return <motion.button whileTap={{
    scale: 0.98
  }} className={`${baseStyle} ${variants[variant as keyof typeof variants]} ${className}`} {...props} data-id="element-172">
      {children}
    </motion.button>;
};